import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Lock, Loader2, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";
import { TID } from "@/constants/testIds";
import { authApi } from "@/lib/api";

const CLINIC_IMG =
  "https://images.unsplash.com/photo-1756679208632-573833f69f8c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBjbGluaWMlMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc4NjE5MTk3Nnww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = "Enter a valid email address";
    if (!password || password.length < 4) e.password = "Password must be at least 4 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await authApi.login({ email, password });
      if (res?.requiresOtp || res?.success) {
        toast.success("Verification code sent", { description: "We emailed an 8-digit code to your registered email." });
        sessionStorage.setItem("sa_login_email", email);
        navigate("/verify-otp");
      } else {
        toast.error("Login failed", { description: "Unexpected response from server." });
      }
    } catch (err) {
      toast.error("Login failed", {
        description: err?.response?.data?.message || "Please check your credentials and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lux-grain relative flex min-h-screen bg-background">
      <div className="absolute right-5 top-5 z-20">
        <ThemeToggle />
      </div>

      {/* Left — form */}
      <div className="relative z-10 flex w-full items-center justify-center px-6 py-12 lg:w-1/2">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="mb-8 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-lux-emerald/15 text-lux-emerald">
              <Stethoscope className="h-6 w-6" />
            </div>
            <div className="leading-tight">
              <p className="font-heading text-xl font-semibold tracking-tight">CliniQueue</p>
              <p className="text-[11px] font-medium uppercase tracking-widest text-gold">Superadmin Portal</p>
            </div>
          </div>

          <h1 className="font-heading text-4xl font-semibold tracking-tight sm:text-5xl">Welcome back</h1>
          <p className="mt-3 text-muted-foreground">
            Sign in to oversee clinics, revenue and applications.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  data-testid={TID.loginEmail}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@clinic.com"
                  className="h-12 border-border bg-secondary/30 pl-10"
                />
              </div>
              {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  data-testid={TID.loginPassword}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 border-border bg-secondary/30 pl-10"
                />
              </div>
              {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
            </div>

            <Button
              data-testid={TID.loginSubmit}
              type="submit"
              disabled={loading}
              className="h-12 w-full rounded-full bg-lux-emerald text-base font-semibold text-[#0a2417] transition-colors hover:bg-lux-emeraldHover"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Continue"}
            </Button>
          </form>
        </motion.div>
      </div>

      {/* Right — visual */}
      <div className="relative hidden w-1/2 overflow-hidden lg:block">
        <img src={CLINIC_IMG} alt="Clinic" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#05070c] via-[#05070c]/70 to-transparent" />
        <div className="absolute inset-0 bg-lux-emerald/5" />
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <motion.blockquote
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="max-w-md"
          >
            <p className="font-heading text-2xl font-medium leading-snug text-white">
              "Oversight, revenue and moderation — refined into one elegant command center."
            </p>
            <footer className="mt-4 text-sm font-medium text-white/70">
              CliniQueue | SUPER ADMIN
            </footer>
          </motion.blockquote>
        </div>
      </div>
    </div>
  );
}
